import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppUser } from '../../../hooks/useAppUser';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import { usePageSize } from '../../../stores/settingsStore';

/**
 * Custom hook for debounced value
 * Delays updating the value until user stops typing
 */
function useDebouncedValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function Rooms() {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAppUser();
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage } = usePageSize('rooms');
  const [search, setSearch] = useState('');

  // Debounce search to prevent API calls on every keystroke
  const debouncedSearch = useDebouncedValue(search, 300);

  // Include user ID in query keys so cache is per-user
  const userId = user?.id || user?._id || user?.email;

  const { data, isLoading, error, isError, refetch } = useQuery({
    queryKey: ['rooms', userId, page, rowsPerPage, debouncedSearch],
    queryFn: () => api.get('/api/rooms', { skip: page * rowsPerPage, limit: rowsPerPage, search: debouncedSearch }),
    enabled: !!userId,
    placeholderData: (previousData) => previousData,
    retry: 2,
  });

  const rooms = data?.rooms || [];
  const total = data?.total || 0;

  // Reset page when user changes
  useEffect(() => {
    setPage(0);
  }, [userId]);

  // Reset page when debounced search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  // Handle search input change (immediate UI update, debounced API call)
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Typography variant="h4" gutterBottom>
        Rooms
      </Typography>
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder="Search rooms..."
          value={search}
          onChange={handleSearchChange}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }
          }}
        />
      </Paper>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', mb: 7 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Room Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Participants</TableCell>
                <TableCell>Recording</TableCell>
                <TableCell>Streaming</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={() => refetch()}
                        >
                          Retry
                        </Button>
                      }
                      sx={{ justifyContent: 'center' }}
                    >
                      {error?.message || 'Failed to load rooms. Please try again.'}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : rooms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>No rooms found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rooms.map((room) => (
                  <TableRow
                    key={room._id || room.id}
                    hover
                    onDoubleClick={() => navigate(`/rooms/${room._id || room.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{room.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: 200 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {room.owner || '—'}
                        </Typography>
                        {room.ownerSubscriptionStatus && (
                          <Tooltip title={room.ownerSubscriptionStatus === 'trialing' ? 'Owner is on a trial' : 'Owner has an active subscription'}>
                            <Chip
                              label={room.ownerSubscriptionStatus === 'trialing' ? 'Trialing' : 'Subscribed'}
                              color={room.ownerSubscriptionStatus === 'trialing' ? 'info' : 'success'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={room.active ? 'Active' : 'Ended'}
                        color={room.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{room.participants || room.userIds?.length || 0}</TableCell>
                    <TableCell>
                      {room.recordings > 0 ? (
                        <Chip
                          icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                          label={room.recordings}
                          size="small"
                          color={room.recordingActive ? 'error' : 'default'}
                          variant={room.recordingActive ? 'filled' : 'outlined'}
                        />
                      ) : (
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {room.streamings > 0 ? (
                        <Chip
                          icon={<LiveTvIcon sx={{ fontSize: 14 }} />}
                          label={room.streamings}
                          size="small"
                          color={room.streamingActive ? 'primary' : 'default'}
                          variant={room.streamingActive ? 'filled' : 'outlined'}
                        />
                      ) : (
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {dayjs(room.timeStarted).format('MMM D, YYYY h:mm A')}
                    </TableCell>
                    <TableCell>
                      {room.timeEnded ? (
                        `${dayjs(room.timeEnded).diff(dayjs(room.timeStarted), 'minute')} min`
                      ) : (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 500,
                          }}
                        >
                          {dayjs().diff(dayjs(room.timeStarted), 'minute')} min+
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1100,
        }}
      />
    </Box>
  );
}

export default Rooms;
